<template>
  <div class="d-flex syrenis-grey nav-container">
    <v-navigation-drawer
      permanent
      width="260"
      class="navigation-drawer"
    >
      <!-- Logo - switches based on theme -->
      <img
        :src="currentLogo"
        class="ma-4 nav-full-logo"
        alt="Syrenis Logo"
      >

      <v-list
        nav
        dense
      >
        <!-- Note: Original uses {{ link.text | useLabels }} for i18n.
             This component library version displays raw text.
             Implement a custom filter or prop transform if localisation is needed. -->
        <template v-for="link in links">
          <!-- Simple link without sublinks -->
          <v-list-item
            v-if="!link.sublinks || link.sublinks.length === 0"
            :key="link.text"
            :to="link.customAction ? undefined : link.route"
            :link="!link.customAction"
            @click="triggerCustomAction(link.customAction)"
          >
            <v-list-item-title class="syrenis-black--text">
              <lucide-icon
                :name="mapIcon(link.icon)"
                :size="18"
                class="nav-icon-color--text mr-3"
                aria-hidden="true"
              />
              <span class="nav-text-with-chip">
                <span>{{ link.text }}</span>
                <v-chip
                  v-if="link.chipLabel"
                  class="nav-chip"
                  color="steel-blue"
                  x-small
                >
                  {{ link.chipLabel }}
                </v-chip>
              </span>
            </v-list-item-title>
          </v-list-item>

          <!-- Link with sublinks -->
          <v-list-group
            v-else
            ref="mainListGroups"
            :key="link.text"
            link
            :ripple="false"
          >
            <v-list-item-content
              slot="activator"
              color="syrenis-black"
            >
              <v-list-item-title class="syrenis-black--text">
                <lucide-icon
                  :name="mapIcon(link.icon)"
                  :size="18"
                  class="nav-icon-color--text mr-3"
                  aria-hidden="true"
                />
                <span class="nav-text-with-chip">
                  <span>{{ link.text }}</span>
                  <v-chip
                    v-if="link.chipLabel"
                    class="nav-chip"
                    color="steel-blue"
                    x-small
                  >
                    {{ link.chipLabel }}
                  </v-chip>
                </span>
              </v-list-item-title>
            </v-list-item-content>

                <!-- Sublinks -->
                <template v-for="sublink in link.sublinks">
                  <!-- Simple sublink without deeper sublinks -->
                  <v-list-item
                    v-if="!sublink.deeperSublinks || sublink.deeperSublinks.length === 0"
                    :key="sublink.text"
                    :to="sublink.customAction ? undefined : sublink.route"
                    :link="!sublink.customAction"
                    @click="triggerCustomAction(sublink.customAction)"
                  >
                    <v-list-item-title class="syrenis-black--text">
                      <span class="nav-text-with-chip">
                        <span>{{ sublink.text }}</span>
                        <v-chip
                          v-if="sublink.chipLabel"
                          class="nav-chip"
                          color="steel-blue"
                          x-small
                        >
                          {{ sublink.chipLabel }}
                        </v-chip>
                      </span>
                    </v-list-item-title>
                  </v-list-item>

                  <!-- Sublink with deeper sublinks -->
                  <v-list-group
                    v-else
                    ref="subListGroups"
                    :key="sublink.text"
                    sub-group
                    class="syrenis-nav-sub-group"
                    :ripple="false"
                  >
                    <v-list-item-content
                      slot="activator"
                      color="indigo"
                    >
                      <v-list-item-title class="syrenis-black--text">
                        <span class="nav-text-with-chip">
                          <span>{{ sublink.text }}</span>
                          <v-chip
                            v-if="sublink.chipLabel"
                            class="nav-chip"
                            color="steel-blue"
                            x-small
                          >
                            {{ sublink.chipLabel }}
                          </v-chip>
                        </span>
                      </v-list-item-title>
                    </v-list-item-content>

                    <!-- Deeper sublinks -->
                    <v-list-item
                      v-for="deeperSublink in sublink.deeperSublinks"
                      :key="deeperSublink.text"
                      :to="deeperSublink.customAction ? undefined : deeperSublink.route"
                      :link="!deeperSublink.customAction"
                      @click="triggerCustomAction(deeperSublink.customAction)"
                    >
                      <v-list-item-title class="syrenis-black--text">
                        <span class="nav-text-with-chip">
                          <span>{{ deeperSublink.text }}</span>
                          <v-chip
                            v-if="deeperSublink.chipLabel"
                            class="nav-chip"
                            color="steel-blue"
                            x-small
                          >
                            {{ deeperSublink.chipLabel }}
                          </v-chip>
                        </span>
                      </v-list-item-title>
                    </v-list-item>
                  </v-list-group>
                </template>
          </v-list-group>
        </template>
      </v-list>
    </v-navigation-drawer>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import type { PropType } from 'vue';
import type { NavLink } from './types';
import LucideIcon from '../LucideIcon';
// Import logos using ES modules for Vite/Storybook compatibility
import fullLogo from '../../assets/images/Syrenis-Black.svg';
import darkLogo from '../../assets/images/Syrenis-red.svg';

/**
 * Maps Material Design Icons (MDI) names to Lucide icon names.
 * Provides a mapping from the mdi-* icon format to Lucide icon names.
 * Falls back to 'Circle' if no mapping is found.
 */
function mapMdiToLucide(mdiIcon: string | null | undefined): string {
  if (!mdiIcon) return 'Circle';

  // Remove 'mdi-' prefix if present
  const iconName = mdiIcon.replace(/^mdi-/, '');

  // Common icon mappings from MDI to Lucide
  const iconMap: Record<string, string> = {
    // Navigation & UI
    'home': 'Home',
    'home-outline': 'Home',
    'menu': 'Menu',
    'view-grid': 'LayoutGrid',
    'view-grid-outline': 'LayoutGrid',
    'view-dashboard': 'LayoutDashboard',
    'view-dashboard-outline': 'LayoutDashboard',
    'dots-vertical': 'MoreVertical',
    'dots-horizontal': 'MoreHorizontal',
    'arrow-left': 'ArrowLeft',
    'arrow-right': 'ArrowRight',
    'arrow-up': 'ArrowUp',
    'arrow-down': 'ArrowDown',
    'chevron-left': 'ChevronLeft',
    'chevron-right': 'ChevronRight',
    'chevron-up': 'ChevronUp',
    'chevron-down': 'ChevronDown',
    'chevron-double-left': 'ChevronsLeft',
    'chevron-double-right': 'ChevronsRight',
    'chevron-double-up': 'ChevronsUp',
    'chevron-double-down': 'ChevronsDown',
    'close': 'X',
    'close-circle': 'XCircle',
    'plus': 'Plus',
    'plus-circle': 'PlusCircle',
    'minus': 'Minus',
    'minus-circle': 'MinusCircle',

    // Common actions
    'check': 'Check',
    'check-circle': 'CheckCircle',
    'check-circle-outline': 'CheckCircle',
    'alert': 'AlertTriangle',
    'alert-circle': 'AlertCircle',
    'alert-circle-outline': 'AlertCircle',
    'information': 'Info',
    'information-outline': 'Info',
    'help-circle': 'HelpCircle',
    'help-circle-outline': 'HelpCircle',

    // Content & Files
    'file': 'File',
    'file-outline': 'File',
    'file-document': 'FileText',
    'file-document-outline': 'FileText',
    'folder': 'Folder',
    'folder-outline': 'Folder',
    'folder-open': 'FolderOpen',
    'folder-open-outline': 'FolderOpen',
    'download': 'Download',
    'upload': 'Upload',
    'cloud-download': 'CloudDownload',
    'cloud-upload': 'CloudUpload',

    // Settings & Tools
    'cog': 'Settings',
    'cog-outline': 'Settings',
    'tune': 'SlidersHorizontal',
    'wrench': 'Wrench',
    'hammer-wrench': 'Wrench',
    'tools': 'Wrench',

    // Communication
    'email': 'Mail',
    'email-outline': 'Mail',
    'message': 'MessageSquare',
    'message-outline': 'MessageSquare',
    'bell': 'Bell',
    'bell-outline': 'Bell',
    'phone': 'Phone',
    'phone-outline': 'Phone',

    // User & Account
    'account': 'User',
    'account-outline': 'User',
    'account-circle': 'UserCircle',
    'account-circle-outline': 'UserCircle',
    'account-group': 'Users',
    'account-group-outline': 'Users',
    'account-multiple': 'Users',
    'account-multiple-outline': 'Users',

    // Data & Analytics
    'chart-bar': 'BarChart',
    'chart-line': 'LineChart',
    'chart-pie': 'PieChart',
    'poll': 'BarChart',
    'analytics': 'BarChart',
    'chart-timeline': 'Activity',

    // Calendar & Time
    'calendar': 'Calendar',
    'calendar-outline': 'Calendar',
    'calendar-blank': 'Calendar',
    'calendar-blank-outline': 'Calendar',
    'clock': 'Clock',
    'clock-outline': 'Clock',

    // Security
    'lock': 'Lock',
    'lock-outline': 'Lock',
    'lock-open': 'LockOpen',
    'lock-open-outline': 'LockOpen',
    'shield': 'Shield',
    'shield-outline': 'Shield',
    'key': 'Key',
    'key-outline': 'Key',

    // Edit & Delete
    'pencil': 'Pencil',
    'pencil-outline': 'Pencil',
    'delete': 'Trash2',
    'delete-outline': 'Trash2',
    'content-copy': 'Copy',
    'content-cut': 'Scissors',
    'content-paste': 'Clipboard',

    // View & Display
    'eye': 'Eye',
    'eye-outline': 'Eye',
    'eye-off': 'EyeOff',
    'eye-off-outline': 'EyeOff',
    'magnify': 'Search',
    'filter': 'Filter',
    'filter-outline': 'Filter',

    // Media
    'image': 'Image',
    'image-outline': 'Image',
    'video': 'Video',
    'video-outline': 'Video',
    'camera': 'Camera',
    'camera-outline': 'Camera',

    // Database & Storage
    'database': 'Database',
    'database-outline': 'Database',
    'server': 'Server',
    'server-outline': 'Server',
    'harddisk': 'HardDrive',

    // Shopping & Commerce
    'cart': 'ShoppingCart',
    'cart-outline': 'ShoppingCart',
    'tag': 'Tag',
    'tag-outline': 'Tag',
    'credit-card': 'CreditCard',
    'credit-card-outline': 'CreditCard',

    // Location & Map
    'map': 'Map',
    'map-outline': 'Map',
    'map-marker': 'MapPin',
    'map-marker-outline': 'MapPin',
    'compass': 'Compass',
    'compass-outline': 'Compass',

    // Miscellaneous
    'star': 'Star',
    'star-outline': 'Star',
    'heart': 'Heart',
    'heart-outline': 'Heart',
    'flag': 'Flag',
    'flag-outline': 'Flag',
    'bookmark': 'Bookmark',
    'bookmark-outline': 'Bookmark',
    'link': 'Link',
    'link-variant': 'Link2',
    'refresh': 'RefreshCw',
    'sync': 'RefreshCw',
    'history': 'History',
    'package': 'Package',
    'package-variant': 'Package',
    'cube': 'Box',
    'cube-outline': 'Box',
    'layers': 'Layers',
    'layers-outline': 'Layers',

    // Admin portal specific icons
    'grain': 'Grip',
    'order-bool-descending-variant': 'ListChecks',
    'cookie': 'Cookie',
    'cookie-outline': 'Cookie',
    'chart-line-variant': 'TrendingUp',
    'login': 'LogIn',
    'logout': 'LogOut',
    'headset': 'Headphones',
    'headset-outline': 'Headphones',
    'face': 'Smile',
    'face-agent': 'Headphones',
    'help-box': 'HelpCircle',
    'help-box-outline': 'HelpCircle',
    'school': 'GraduationCap',
    'school-outline': 'GraduationCap'
  };

  return iconMap[iconName] || 'Circle';
}

export default Vue.extend({
  name: 'ModernNavBar',
  components: {
    LucideIcon
  },
  props: {
    links: {
      type: Array as PropType<NavLink[]>,
      default: () => []
    },
    changeShowFilters: {
      type: Function as PropType<(show: boolean) => void>,
      default: () => {}
    },
    resetAdminPortalFilters: {
      type: Function as PropType<() => void>,
      default: () => {}
    },
    fullLogoSrc: {
      type: String,
      default: fullLogo
    },
    darkLogoSrc: {
      type: String,
      default: darkLogo
    }
  },
  data() {
    return {
      isDarkMode: false
    };
  },
  computed: {
    currentLogo(): string {
      return this.isDarkMode ? this.darkLogoSrc : this.fullLogoSrc;
    }
  },
  mounted() {
    this.checkDarkMode();
    // Watch for theme changes
    this.observeThemeChanges();
  },
  beforeDestroy() {
    if (this.themeObserver) {
      this.themeObserver.disconnect();
    }
  },
  methods: {
    checkDarkMode() {
      this.isDarkMode = document.documentElement.classList.contains('dark');
    },
    observeThemeChanges() {
      // Create a MutationObserver to watch for class changes on documentElement
      const observer = new MutationObserver(() => {
        this.checkDarkMode();
      });

      observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class']
      });

      // Store observer reference for cleanup
      (this as any).themeObserver = observer;
    },
    triggerCustomAction(action?: () => void) {
      this.resetFilters();
      if (!action) return;
      action();
    },
    resetFilters() {
      this.changeShowFilters(false);
      this.resetAdminPortalFilters();
    },
    mapIcon(mdiIcon: string | null): string {
      return mapMdiToLucide(mdiIcon);
    }
  }
});
</script>

<style lang="scss" scoped>
@import './styles.scss';
</style>
